import { Component, OnInit } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { BehaviorSubject } from 'rxjs';
import { PolicyPrice } from '../../policies/models/Policy';
import { PriceRatesService } from '../services/price-rates.service';
import { Currency } from '../../currencies/models/Currency';
import { CurrenciesService } from '../../currencies/services/currencies.service';
import { Logger } from '../../../infrastructure/logging/logger';

@Component({
  selector: 'app-price-rate-form',
  templateUrl: './price-rate-form.component.html',
  styleUrls: ['./price-rate-form.component.scss'],
  providers: [ CurrenciesService, CurrencyPipe ],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatIcon,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatOption,
    MatExpansionModule,
    RouterModule
  ]
})
export class PriceRateFormComponent implements OnInit {

  priceTypesArray: any[] = [
    { id: 1, display_text: 'Underwriter' },
    { id: 2, display_text: 'Property Manager' }
  ]

  pricingModels: any[] = [
    { id: 1, display_text: 'Tiered Nights' },
    { id: 2, display_text: 'Per Night' }
  ]

  loading = new BehaviorSubject<boolean>(false);
  form!: FormGroup;
  tieredNightForm!: FormGroup;
  perNightForm!: FormGroup;
  perReservationForm!: FormGroup;
  priceRate!: PolicyPrice;
  priceRateId: any;
  allPriceRates: PolicyPrice[] = [];
  priceRateNameExists: boolean = false;
  existingPriceRate: boolean = false;
  currencies!: Currency[];
  numberOfNightsToInput: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private priceRateService: PriceRatesService,
    private currenciesService: CurrenciesService,
    private logService: Logger,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.priceRateId = this.getPriceRateIdFromUrl();
    this.initForm();
    this.loadPriceRateFormData();
  }

  async loadPriceRateFormData() {
    try {
      this.loading.next(true);

      const currenciesResponse = await this.currenciesService.getCurrencies();
      this.currencies = currenciesResponse;

      this.allPriceRates = await this.priceRateService.getPriceRates();

      if (this.priceRateId) {
        this.priceRateService.getPriceRateDetail(this.priceRateId).then(async(priceRate: PolicyPrice) => {
          this.loadFromPriceRate(priceRate);
          this.priceRate = priceRate;
          this.existingPriceRate = true;
        }).finally(() => {
          if (this.loadingComplete()) {
            this.loading.next(false);
          }
        });
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Price Rate form', 'Error while retrieving Price Rate form');
    } finally {
      this.loading.next(false);
    }
  }

  async loadFromPriceRate(priceRate: PolicyPrice) {
    this.form.patchValue(priceRate);
    this.form.controls['type_id'].setValue(priceRate.type_id.toString());
    this.form.controls['pricing_model_id'].setValue(priceRate.pricing_model_id.toString());
    this.form.controls['currency_id'].setValue(priceRate.currency_id.toString());
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    if (priceRate.pricing_model_id === 1) {
      this.tieredNightForm.controls['extra_night_price'].setValue(priceRate.extra_night_price);

      const priceArray = this.tieredNightForm.get("night_ranges") as FormArray;
      priceRate.night_ranges.forEach(range => {
        const nightRangeGroup = this.newNightRange();
        Object.keys(nightRangeGroup.controls).forEach(key => {
          nightRangeGroup.patchValue({
            [key]: range[key as keyof Object]
          });
        });
        priceArray.controls.push(nightRangeGroup);
      });

      this.tieredNightForm.markAllAsTouched();
      this.tieredNightForm.markAsDirty();
    }

    if (priceRate.pricing_model_id === 2) {
      this.perNightForm.patchValue(priceRate);
      this.perNightForm.controls['nightly_price'].setValue(priceRate.price);
      this.perNightForm.markAllAsTouched();
      this.perNightForm.markAsDirty();
    }

    // TODO: Remove all perReservation form when testing with new data
    if (priceRate.pricing_model_id === 3) {
      this.perReservationForm.controls['per_reservation_price'].setValue(priceRate.price);
      this.perReservationForm.markAllAsTouched();
      this.perReservationForm.markAsDirty();
    }
  }

  initForm() {
    this.initSubForms();
    this.form = this.formBuilder.group({
      name: ['', {
        validators: [Validators.required, this.checkForDuplicatePriceRateName()],
        updateOn: 'change'
      }],
      type_id: ['', Validators.required],
      pricing_model_id: ['', Validators.required],
      currency_id: ['', Validators.required],
      deductible: ['', Validators.required],
      description: ['']
    });
  }

  initSubForms() {
    this.tieredNightForm = this.formBuilder.group({
      night_ranges: this.formBuilder.array([
        this.formBuilder.group({
          name: ['', Validators.required],
          number_of_nights_from: [{ value: 1, disabled: true }, [Validators.required]],
          number_of_nights_to: ['', Validators.required],
          price: ['', Validators.required]
        })
      ]),
      extra_night_price: ['', Validators.required]
    });

    if (this.priceRateId) {
      const priceArray = this.tieredNightForm.get("night_ranges") as FormArray;
      priceArray.removeAt(0);
    }

    this.perNightForm = this.formBuilder.group({
      nightly_price: ['', Validators.required]
    });

    this.perReservationForm = this.formBuilder.group({
      per_reservation_price: ['', Validators.required]
    });
  }

  checkForDuplicatePriceRateName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.allPriceRates.some(priceRate => priceRate.name.toLowerCase() === control.value.toLowerCase())) {
        this.priceRateNameExists = true;
      } else {
        this.priceRateNameExists = false;
        false;
      }
      return this.priceRateNameExists ? { invalidPriceRateName: this.priceRateNameExists } : null;
    };
  }

  nightRanges(): FormArray {
    return this.tieredNightForm.get('night_ranges') as FormArray;
  }

  newNightRange(): FormGroup {
    return this.formBuilder.group({
      name: ['', Validators.required],
      number_of_nights_from: [{ value: Number(this.numberOfNightsToInput) + 1, disabled: true }, Validators.required],
      number_of_nights_to: ['', Validators.required],
      price: ['', Validators.required]
    });
  }

  addNightRange() {
    this.nightRanges().push(this.newNightRange());
  }

  removeNightRange(i: number) {
    this.nightRanges().removeAt(i);
  }

  setTieredNightName(i: number, nightsTo: any) {
    this.numberOfNightsToInput = nightsTo;

    let nightRangeArray = this.tieredNightForm.get("night_ranges") as FormArray;
    nightRangeArray.controls[i].patchValue({
      name: nightsTo + '-nights'
    });

    nightRangeArray.controls[i + 1].patchValue({
      number_of_nights_from: Number(nightsTo) + 1
    });
  }

  async submitPriceRate() {
    this.loading.next(true);

    try {
      let priceRatePayload = await this.buildPriceRate();

      if (this.existingPriceRate) {
        this.priceRateId = (await this.priceRateService.updatePriceRate(this.priceRateId, priceRatePayload)).id;
      } else {
        const priceRateId = (await this.priceRateService.createPriceRate(priceRatePayload)).id;
        this.priceRateId = priceRateId;
        this.existingPriceRate = true;
      }

      if (!this.priceRateId) {
        this.logService.handleError('Error while submitting Price Rate - Price Rate ID does not exist', 'An error occurred while submitting Price Rate', 'Error while submitting Price Rate');
      } else {
        this.navigateToPriceRateDetailPage(this.priceRateId);
      }
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while submitting Price Rate', 'Error while submitting Price Rate');
    } finally {
      this.loading.next(false);
    }
  }

  async buildPriceRate() {
    try {
      let priceRate: any = {
        id: this.existingPriceRate ? Number(this.priceRateId) : null,
        ...this.form.value
      }

      if (this.existingPriceRate) {
        priceRate.account_id = this.priceRate.account_id;
        priceRate.original_id = this.priceRate.original_id;
        priceRate.is_active = this.priceRate.is_active;
        priceRate.keep_history = 1;
        priceRate.status_id = 2;
        priceRate.updated_date = new Date();
      }

      if (this.form.get('pricing_model_id')?.value === '1') {
        priceRate = { ...priceRate, ...this.tieredNightForm.getRawValue() }
        delete priceRate.perNightForm;
      }
      if (this.form.get('pricing_model_id')?.value === '2') {
        priceRate.price = this.perNightForm.get('nightly_price')?.value;
        delete priceRate.tieredNightForm;
      }

      return priceRate;
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Price Rate', 'Error while submitting Price Rate');
      return null;
    }
  }

  loadingComplete() {
    let currenciesLoaded: boolean = this.currencies !== undefined && this.currencies !== null && this.currencies.length > 0;
    return currenciesLoaded;
  }

  getPriceRateIdFromUrl() {
    return this.route.snapshot.queryParamMap.get('id');
  }

  canCreatePriceRate(): boolean {
    return (
      this.form.valid && 
      this.tieredNightForm.valid || 
      this.perNightForm.valid
    );
  }

  navigateToPriceRateDetailPage(priceRateId: string) {
    this.router.navigate([`/price-rates/detail/${priceRateId}`]);
  }
}